"use client";

import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { useAction, useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { CancellationConfirmationModal } from "@/components/billing/CancellationConfirmationModal";
import { usePageTitle } from "@/lib/usePageTitle";
import { useSubscription } from "@/hooks/useSubscription";
import { useCheckout } from "@/hooks/useCheckout";
import {
  PRICING_PLANS,
  TRIAL_DAYS,
  SERVER_LIMITS_BY_TIER,
  type PricingPlanId,
  type SubscriptionTier,
  type SubscriptionStatus,
} from "@/lib/billing/plans";

// ─── Helpers ───────────────────────────────────────────────────────────────────

function tierLabel(tier: SubscriptionTier): string {
  switch (tier) {
    case "starter": return "Founder Starter";
    case "plus": return "Founder Plus";
    case "pro": return "Founder Pro";
    case "enterprise": return "Enterprise";
    default: return "Free";
  }
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function daysUntil(ts: number): number {
  return Math.max(0, Math.ceil((ts - Date.now()) / (1000 * 60 * 60 * 24)));
}

const PLAN_ORDER = ["starter", "plus", "pro"];

// ─── Sub-components ────────────────────────────────────────────────────────────

function StatusPill({
  status,
  cancelAtPeriodEnd,
  isInGracePeriod,
}: {
  status: SubscriptionStatus;
  cancelAtPeriodEnd: boolean;
  isInGracePeriod: boolean;
}) {
  if (isInGracePeriod) {
    return (
      <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
        Grace Period
      </span>
    );
  }
  if (cancelAtPeriodEnd) {
    return (
      <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
        Cancellation Scheduled
      </span>
    );
  }
  if (status === "active") {
    return (
      <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700 dark:bg-green-900/30 dark:text-green-400">
        Active
      </span>
    );
  }
  if (status === "trialing") {
    return (
      <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
        Free Trial
      </span>
    );
  }
  if (status === "past_due") {
    return (
      <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-400">
        Past Due
      </span>
    );
  }
  if (status === "canceled") {
    return (
      <span className="rounded-full bg-foreground/10 px-3 py-1 text-xs font-semibold text-muted">
        Canceled
      </span>
    );
  }
  return (
    <span className="rounded-full bg-foreground/10 px-3 py-1 text-xs font-semibold text-muted">
      Free Plan
    </span>
  );
}

function CheckIcon() {
  return (
    <svg className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function UsageBar({ used, limit }: { used: number; limit: number }) {
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const isNearLimit = pct >= 80;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-muted">
        <span>{used} of {limit} used</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-foreground/10">
        <div
          className={`h-full rounded-full transition-all ${isNearLimit ? "bg-amber-500" : "bg-primary"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ─── Billing History helpers ──────────────────────────────────────────────────

function eventTypeLabel(eventType: string): string {
  switch (eventType) {
    case "created": return "Subscription created";
    case "activated": return "Subscription activated";
    case "updated": return "Plan updated";
    case "upgraded": return "Plan upgraded";
    case "downgraded": return "Plan downgraded";
    case "cancelled": return "Subscription cancelled";
    case "renewed": return "Subscription renewed";
    case "payment_failed": return "Payment failed";
    case "paused": return "Subscription paused";
    case "resumed": return "Subscription resumed";
    default: return eventType;
  }
}

function eventTypeIcon(eventType: string) {
  switch (eventType) {
    case "created":
    case "activated":
    case "renewed":
      return (
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
          <svg className="h-3.5 w-3.5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      );
    case "upgraded":
      return (
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
          <svg className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </div>
      );
    case "downgraded":
      return (
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
          <svg className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      );
    case "cancelled":
    case "payment_failed":
      return (
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
          <svg className="h-3.5 w-3.5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      );
    case "paused":
      return (
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-foreground/10">
          <svg className="h-3.5 w-3.5 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6" />
          </svg>
        </div>
      );
    default:
      return (
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-foreground/10">
          <svg className="h-3.5 w-3.5 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01" />
          </svg>
        </div>
      );
  }
}

function formatTimestamp(ts: number): string {
  return new Date(ts).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function BillingHistorySection({ clerkUserId }: { clerkUserId: string | null }) {
  const auditLog = useQuery(
    api.billing.getSubscriptionAuditLog,
    clerkUserId ? { limit: 10 } : "skip"
  );

  if (auditLog === undefined) {
    return (
      <div className="rounded-lg border border-border bg-background p-6">
        <h2 className="mb-4 text-lg font-semibold">Billing History</h2>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="h-7 w-7 animate-pulse rounded-full bg-foreground/10" />
              <div className="flex-1 space-y-1">
                <div className="h-3.5 w-32 animate-pulse rounded bg-foreground/10" />
                <div className="h-3 w-24 animate-pulse rounded bg-foreground/5" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!auditLog || auditLog.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-background p-6">
        <h2 className="mb-1 text-lg font-semibold">Billing History</h2>
        <p className="text-sm text-muted">
          No billing events yet. Your subscription activity will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-background p-6">
      <h2 className="mb-4 text-lg font-semibold">Billing History</h2>
      <div className="space-y-0">
        {auditLog.map((event, idx) => (
          <div
            key={event._id}
            className={`flex items-start gap-3 py-3 ${idx !== auditLog.length - 1 ? "border-b border-border" : ""}`}
          >
            {eventTypeIcon(event.eventType)}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-foreground">
                  {eventTypeLabel(event.eventType)}
                </span>
                {event.newStatus && (
                  <span className="rounded-full bg-foreground/5 px-2 py-0.5 text-[11px] text-muted">
                    {event.newStatus}
                  </span>
                )}
              </div>
              <div className="mt-0.5 text-xs text-muted">
                {formatTimestamp(event.timestamp)}
                {event.previousStatus && event.previousStatus !== event.newStatus && (
                  <span> &middot; {event.previousStatus} &rarr; {event.newStatus}</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function SubscriptionManagementPage() {
  usePageTitle("Subscription");

  const { userId: clerkUserId } = useAuth();
  const sub = useSubscription();
  const openPortal = useAction(api.actions.openCustomerPortal);
  const openStripePortal = useAction(api.actions.openStripeCustomerPortal);
  const resumeSub = useAction(api.actions.resumeSubscription);
  const scheduleCancellation = useAction(api.actions.scheduleSubscriptionCancellation);
  const { startCheckout, loadingPlan, error: checkoutError } = useCheckout();

  const [portalLoading, setPortalLoading] = useState(false);
  const [stripePortalLoading, setStripePortalLoading] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [actionError, setActionError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Plan change confirmation state
  const [pendingPlanChange, setPendingPlanChange] = useState<{
    planId: PricingPlanId;
    isUpgrade: boolean;
    isDowngrade: boolean;
  } | null>(null);

  if (sub.isLoading) {
    return (
      <div className="mx-auto max-w-4xl space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-7 w-40 animate-pulse rounded bg-foreground/10" />
            <div className="mt-1 h-4 w-64 animate-pulse rounded bg-foreground/5" />
          </div>
        </div>
        <div className="h-48 animate-pulse rounded-lg bg-foreground/5" />
        <div className="h-64 animate-pulse rounded-lg bg-foreground/5" />
      </div>
    );
  }

  const tier = sub.tier;
  const status = sub.status;
  const hasSubscription = status !== "none" && tier !== "free";
  const isActiveSubscriber = sub.hasAccess && (status === "active" || status === "trialing");
  const serverLimit = SERVER_LIMITS_BY_TIER[tier] ?? 0;
  // Show portal link only for active or cancelling (not yet expired) subscribers
  const canUsePortal = hasSubscription && (status === "active" || status === "trialing" || status === "canceling" || status === "past_due" || (status === "canceled" && sub.currentPeriodEnd !== null && (sub.currentPeriodEnd ?? 0) > Date.now()));

  const handleOpenPortal = async () => {
    setActionError("");
    setSuccessMessage("");
    setPortalLoading(true);
    try {
      const result = await openPortal({});
      if (!result.ok) throw new Error(result.error?.message || "Unable to open portal.");
      if (result.data?.url) window.location.href = result.data.url;
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Unable to open customer portal.");
    } finally {
      setPortalLoading(false);
    }
  };

  const handleOpenStripePortal = async () => {
    setActionError("");
    setSuccessMessage("");
    setStripePortalLoading(true);
    try {
      const result = await openStripePortal({});
      if (!result.ok) throw new Error(result.error?.message || "Unable to open portal.");
      if (result.data?.url) window.location.href = result.data.url;
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Unable to open Stripe customer portal.");
    } finally {
      setStripePortalLoading(false);
    }
  };

  const handleResume = async () => {
    setActionError("");
    setSuccessMessage("");
    setResumeLoading(true);
    try {
      const result = await resumeSub();
      if (!result.ok) throw new Error(result.error?.message || "Resume failed.");
      setSuccessMessage("Auto-renew has been re-enabled. Your subscription will continue past the current period.");
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to resume subscription.");
    } finally {
      setResumeLoading(false);
    }
  };

  const handleConfirmCancel = async () => {
    setCancelling(true);
    setActionError("");
    setSuccessMessage("");
    try {
      const result = await scheduleCancellation();
      if (!result.ok) throw new Error(result.error?.message || "Cancellation failed.");
      setShowCancelModal(false);
      setSuccessMessage("Cancellation scheduled. You'll keep full access until your billing period ends.");
    } catch (err) {
      setShowCancelModal(false);
      setActionError(err instanceof Error ? err.message : "Failed to schedule cancellation.");
    } finally {
      setCancelling(false);
    }
  };

  const handleSelectPlan = (planId: PricingPlanId) => {
    setActionError("");
    setSuccessMessage("");
    const isTrialEligible = planId === "starter" && !sub.hasUsedTrial && tier === "free";

    if (isActiveSubscriber) {
      const currentIdx = PLAN_ORDER.indexOf(tier);
      const targetIdx = PLAN_ORDER.indexOf(planId);
      setPendingPlanChange({
        planId,
        isUpgrade: targetIdx > currentIdx,
        isDowngrade: targetIdx < currentIdx,
      });
    } else {
      startCheckout(planId, { withTrial: isTrialEligible });
    }
  };

  const handleConfirmPlanChange = async () => {
    if (!pendingPlanChange) return;
    const { planId } = pendingPlanChange;
    setPendingPlanChange(null);

    await startCheckout(planId, {
      onPlanUpdated: () => {
        const planDef = PRICING_PLANS.find((p) => p.id === planId);
        setSuccessMessage(`Plan updated to ${planDef?.name ?? planId}. Changes are live immediately.`);
      },
    });
  };

  // Build confirmation dialog copy
  const getConfirmDialogProps = () => {
    if (!pendingPlanChange) return { title: "", description: "", confirmText: "" };
    const planDef = PRICING_PLANS.find((p) => p.id === pendingPlanChange.planId);
    const planName = planDef?.name ?? pendingPlanChange.planId;
    if (pendingPlanChange.isUpgrade) {
      return {
        title: `Upgrade to ${planName}?`,
        description: `Your plan switches to ${planName} ($${planDef?.priceMonthly.toFixed(2) ?? "?"}/mo) immediately. A new billing cycle starts now.`,
        confirmText: `Upgrade to ${planName}`,
      };
    }
    if (pendingPlanChange.isDowngrade) {
      return {
        title: `Downgrade to ${planName}?`,
        description: `Your plan switches to ${planName} ($${planDef?.priceMonthly.toFixed(2) ?? "?"}/mo) immediately with prorated credit. Some features may become unavailable.`,
        confirmText: `Downgrade to ${planName}`,
      };
    }
    return {
      title: `Switch to ${planName}?`,
      description: `Your plan will be switched to ${planName} immediately.`,
      confirmText: `Switch to ${planName}`,
    };
  };

  const dialogProps = getConfirmDialogProps();

  return (
    <div className="mx-auto max-w-4xl space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Subscription</h1>
          <p className="text-sm text-muted">Manage your BlockyNetworks plan</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/dashboard/settings/billing" className="text-sm text-primary hover:underline">
            Full Billing Settings
          </Link>
          <Link to="/dashboard" className="text-sm text-muted hover:underline">
            Dashboard
          </Link>
        </div>
      </div>

      {/* ── Banners ── */}

      {/* Grace period */}
      {sub.gracePeriod.isInGracePeriod && sub.gracePeriod.graceEndsAt && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 dark:border-orange-800 dark:bg-orange-900/20">
          <div className="flex items-start gap-3">
            <svg className="mt-0.5 h-5 w-5 shrink-0 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <div>
              <p className="font-semibold text-orange-800 dark:text-orange-200">
                Grace period — {daysUntil(sub.gracePeriod.graceEndsAt)} day{daysUntil(sub.gracePeriod.graceEndsAt) !== 1 ? "s" : ""} left
              </p>
              <p className="mt-1 text-sm text-orange-700 dark:text-orange-300">
                Your subscription ended but you still have access until{" "}
                <span className="font-medium">{formatDate(sub.gracePeriod.graceEndsAt)}</span>.
                Re-subscribe below to prevent your organizations from being archived.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Trial banner */}
      {sub.isTrialing && sub.trialEndsAt && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-900 dark:text-blue-300">
              Free Trial
            </span>
            <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
              {daysUntil(sub.trialEndsAt)} day{daysUntil(sub.trialEndsAt) !== 1 ? "s" : ""} remaining
            </span>
          </div>
          <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
            Your {TRIAL_DAYS}-day free trial ends on{" "}
            <span className="font-medium">{formatDate(sub.trialEndsAt)}</span>.
            Your saved payment method will be charged automatically if you don&apos;t cancel.
          </p>
        </div>
      )}

      {/* Pending cancellation */}
      {sub.cancelAtPeriodEnd && sub.currentPeriodEnd && !sub.gracePeriod.isInGracePeriod && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-200">Cancellation scheduled</p>
              <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                Your access continues until{" "}
                <span className="font-medium">{formatDate(sub.currentPeriodEnd)}</span>, then you&apos;ll move to the free plan.
              </p>
            </div>
            <Button
              variant="outline"
              className="shrink-0 border-amber-300 text-amber-800 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-200 dark:hover:bg-amber-900/40"
              onClick={handleResume}
              disabled={resumeLoading}
            >
              {resumeLoading ? "Resuming..." : "Resume Subscription"}
            </Button>
          </div>
        </div>
      )}

      {/* Past due */}
      {status === "past_due" && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <p className="font-medium text-red-800 dark:text-red-200">Payment failed</p>
          <p className="mt-1 text-sm text-red-700 dark:text-red-300">
            Your last payment did not go through. Update your payment method via the customer portal to avoid losing access.
          </p>
          <Button
            className="mt-3"
            variant="outline"
            onClick={handleOpenPortal}
            disabled={portalLoading}
          >
            {portalLoading ? "Opening..." : "Update Payment Method →"}
          </Button>
        </div>
      )}

      {/* Success */}
      {successMessage && (
        <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
          <svg className="mt-0.5 h-4 w-4 shrink-0 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <p className="text-sm font-medium text-green-800 dark:text-green-200">{successMessage}</p>
        </div>
      )}

      {/* Error */}
      {(actionError || checkoutError) && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {actionError || checkoutError}
        </div>
      )}

      {/* ── Current plan card ── */}
      <div className="rounded-lg border border-border bg-background p-6">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <h2 className="text-xl font-bold">{tierLabel(tier)}</h2>
              <StatusPill
                status={status}
                cancelAtPeriodEnd={sub.cancelAtPeriodEnd}
                isInGracePeriod={sub.gracePeriod.isInGracePeriod}
              />
            </div>
            <p className="text-sm text-muted">
              {hasSubscription
                ? "Your current BlockyNetworks subscription"
                : "You are on the free plan. Subscribe to unlock server management."}
            </p>
          </div>
          {hasSubscription && (() => {
            const planDef = PRICING_PLANS.find((p) => p.id === tier);
            return planDef ? (
              <div className="text-right">
                <div className="text-2xl font-bold">${planDef.priceMonthly.toFixed(2)}</div>
                <div className="text-xs text-muted">per month</div>
              </div>
            ) : null;
          })()}
        </div>

        {/* Stats grid */}
        {hasSubscription && (
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-md border border-border bg-foreground/5 p-4">
              <div className="text-xs text-muted">Plan</div>
              <div className="mt-1 text-sm font-semibold">{tierLabel(tier)}</div>
            </div>
            <div className="rounded-md border border-border bg-foreground/5 p-4">
              <div className="text-xs text-muted">
                {sub.cancelAtPeriodEnd ? "Access ends" : sub.isTrialing ? "Trial ends" : "Next renewal"}
              </div>
              <div className="mt-1 text-sm font-semibold">
                {sub.currentPeriodEnd
                  ? formatDate(sub.cancelAtPeriodEnd ? sub.currentPeriodEnd : sub.isTrialing ? (sub.trialEndsAt ?? sub.currentPeriodEnd) : sub.currentPeriodEnd)
                  : "—"}
              </div>
              {sub.cancelAtPeriodEnd && (
                <div className="mt-0.5 text-xs text-muted">Then free plan</div>
              )}
            </div>
            <div className="rounded-md border border-border bg-foreground/5 p-4">
              <div className="text-xs text-muted">Status</div>
              <div className="mt-1 text-sm font-semibold capitalize">{status}</div>
            </div>
          </div>
        )}

        {/* Usage bar */}
        {hasSubscription && serverLimit > 0 && (
          <div className="mt-5 border-t border-border pt-4">
            <p className="mb-2.5 text-xs font-medium uppercase tracking-wide text-muted">Server / Org Usage</p>
            <UsageBar used={sub.activeOrgCount} limit={serverLimit} />
            {sub.activeOrgCount >= serverLimit && (
              <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                You&apos;ve reached your server limit. Upgrade to add more.
              </p>
            )}
          </div>
        )}

        {/* Features included */}
        {hasSubscription && (() => {
          const planDef = PRICING_PLANS.find((p) => p.id === tier);
          return planDef?.features.length ? (
            <div className="mt-5 border-t border-border pt-4">
              <p className="mb-2.5 text-xs font-medium uppercase tracking-wide text-muted">What&apos;s included</p>
              <ul className="grid gap-1.5 sm:grid-cols-2">
                {planDef.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs text-muted">
                    <CheckIcon />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ) : null;
        })()}

        {!hasSubscription && (
          <p className="mt-2 text-sm text-muted">
            Choose a plan below to get started.
          </p>
        )}
      </div>

      {/* ── Free tier upgrade CTA ── */}
      {!hasSubscription && (
        <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-bold text-foreground">Unlock Server Management</h2>
              <p className="mt-1 text-sm text-muted">
                {!sub.hasUsedTrial
                  ? `Start your ${TRIAL_DAYS}-day free trial today — no commitment, cancel anytime.`
                  : "Subscribe to create organizations, list servers, and access all BlockyNetworks features."}
              </p>
            </div>
            <Button
              className={`shrink-0 gap-2 px-6 ${!sub.hasUsedTrial ? "bg-green-600 hover:bg-green-700" : ""}`}
              onClick={() => handleSelectPlan(!sub.hasUsedTrial ? "starter" : "plus")}
              disabled={loadingPlan !== null}
            >
              {loadingPlan !== null ? "Processing..." : !sub.hasUsedTrial ? `Start Free Trial` : "Upgrade Now"}
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Button>
          </div>
        </div>
      )}

      {/* ── Stripe billing portal (secondary) ── */}
      {hasSubscription && (
        <div className="rounded-lg border border-border bg-background p-6">
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Stripe Billing Portal</h2>
              <p className="mt-0.5 text-sm text-muted">
                If you subscribed via Stripe, manage your subscription details here.
              </p>
            </div>
            <div className="text-right shrink-0">
              <div className="text-sm font-medium">
                {sub.cancelAtPeriodEnd ? "Cancels" : sub.isTrialing ? "Trial ends" : "Renews"}{" "}
                {sub.currentPeriodEnd ? formatDate(sub.cancelAtPeriodEnd ? sub.currentPeriodEnd : sub.isTrialing ? (sub.trialEndsAt ?? sub.currentPeriodEnd) : sub.currentPeriodEnd) : "—"}
              </div>
              <div className="text-xs text-muted">{tierLabel(tier)} plan</div>
            </div>
          </div>

          <Button
            variant="outline"
            onClick={handleOpenStripePortal}
            disabled={stripePortalLoading}
            className="gap-2"
          >
            {stripePortalLoading ? (
              "Opening portal..."
            ) : (
              <>
                Open Stripe Portal
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </>
            )}
          </Button>

          <p className="mt-3 text-xs text-muted">
            Redirects to Stripe&apos;s secure billing portal. You&apos;ll be returned to this page when done.
          </p>
        </div>
      )}

      {/* ── Plan switcher ── */}
      <div className="rounded-lg border border-border bg-background p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold">
              {isActiveSubscriber ? "Change Plan" : "Choose a Plan"}
            </h2>
            {isActiveSubscriber && (
              <p className="mt-0.5 text-xs text-muted">Plan changes take effect immediately.</p>
            )}
          </div>
        </div>

        {!isActiveSubscriber && !sub.gracePeriod.isInGracePeriod && (
          <p className="mb-5 text-sm text-muted">
            {!sub.hasUsedTrial
              ? `Get started today. The Founder Starter plan includes a ${TRIAL_DAYS}-day free trial for first-time subscribers.`
              : "Get started with a monthly billing cycle."}
          </p>
        )}

        <div className="grid gap-4 lg:grid-cols-3">
          {PRICING_PLANS.map((plan) => {
            const isCurrent = isActiveSubscriber && tier === (plan.id as SubscriptionTier);
            const isTrialEligible = plan.id === "starter" && !sub.hasUsedTrial && tier === "free" && !sub.gracePeriod.isInGracePeriod;
            const hasUsedTrial = sub.hasUsedTrial && plan.id === "starter";
            const currentIdx = PLAN_ORDER.indexOf(tier);
            const targetIdx = PLAN_ORDER.indexOf(plan.id);
            const isUpgrade = isActiveSubscriber && targetIdx > currentIdx;
            const isDowngrade = isActiveSubscriber && targetIdx < currentIdx;

            let buttonLabel = "Subscribe";
            if (isTrialEligible) buttonLabel = `Start ${TRIAL_DAYS}-Day Free Trial`;
            else if (isUpgrade) buttonLabel = `Upgrade to ${plan.name}`;
            else if (isDowngrade) buttonLabel = `Downgrade to ${plan.name}`;
            else if (isActiveSubscriber) buttonLabel = "Switch Plan";
            else if (sub.gracePeriod.isInGracePeriod) buttonLabel = "Re-subscribe";

            return (
              <div
                key={plan.id}
                className={`relative flex flex-col rounded-lg border p-5 transition-colors ${
                  isCurrent
                    ? "border-primary bg-primary/5"
                    : isUpgrade
                      ? "border-border hover:border-primary/40"
                      : "border-border hover:border-primary/30"
                }`}
              >
                {plan.badge && !isCurrent && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                    <span className="rounded-full bg-primary px-2.5 py-0.5 text-[11px] font-semibold text-primary-foreground">
                      {plan.badge}
                    </span>
                  </div>
                )}

                <div className="mb-3 flex flex-wrap items-center gap-1.5">
                  <span className="text-sm font-semibold">{plan.name}</span>
                  {isCurrent && (
                    <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-medium text-primary">
                      Current
                    </span>
                  )}
                  {isTrialEligible && (
                    <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      Trial eligible
                    </span>
                  )}
                  {hasUsedTrial && (
                    <span className="rounded-full bg-foreground/10 px-2 py-0.5 text-[11px] font-medium text-muted">
                      Trial used
                    </span>
                  )}
                  {isUpgrade && (
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                      Upgrade
                    </span>
                  )}
                </div>

                <div className="mb-1 text-2xl font-bold">
                  ${plan.priceMonthly.toFixed(2)}
                  <span className="text-sm font-normal text-muted">/mo</span>
                </div>
                <p className="mb-4 text-xs text-muted">{plan.description}</p>

                <ul className="mb-5 flex-1 space-y-2">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-xs text-muted">
                      <CheckIcon />
                      {feature}
                    </li>
                  ))}
                  {isTrialEligible && (
                    <li className="flex items-start gap-2 text-xs font-medium text-green-600 dark:text-green-400">
                      <CheckIcon />
                      {TRIAL_DAYS}-day free trial (first-time only)
                    </li>
                  )}
                </ul>

                {isCurrent ? (
                  <div className="rounded-md bg-primary/10 px-4 py-2 text-center text-sm font-medium text-primary">
                    Current Plan
                  </div>
                ) : (
                  <Button
                    className={`w-full gap-1.5 ${isTrialEligible ? "bg-green-600 hover:bg-green-700" : ""}`}
                    variant={isDowngrade ? "outline" : "default"}
                    onClick={() => handleSelectPlan(plan.id as PricingPlanId)}
                    disabled={loadingPlan !== null}
                  >
                    {loadingPlan === plan.id ? (
                      "Processing..."
                    ) : (
                      <>
                        {isUpgrade && (
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                          </svg>
                        )}
                        {isDowngrade && (
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                          </svg>
                        )}
                        {buttonLabel}
                      </>
                    )}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        <p className="mt-4 text-xs text-muted">
          Upgrading starts a new billing cycle immediately. Downgrades are immediate with prorated credit applied. No refunds for unused time.
        </p>
      </div>

      {/* ── Manage Subscription card ── */}
      <div className="rounded-lg border border-border bg-background p-6">
        <h2 className="mb-1 text-lg font-semibold">Manage Subscription</h2>
        <p className="mb-4 text-sm text-muted">
          View invoice history, download receipts, and update your saved payment method via the secure Polar customer portal.
        </p>

        <div className="flex flex-wrap gap-3">
          <Button
            onClick={handleOpenPortal}
            disabled={portalLoading || !canUsePortal}
            className="gap-2"
          >
            {portalLoading ? (
              "Opening..."
            ) : (
              <>
                Manage Subscription
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </>
            )}
          </Button>
          <Link
            to="/dashboard/settings/billing"
            className="inline-flex items-center rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-foreground/5"
          >
            Full Billing Settings
          </Link>
        </div>

        {!canUsePortal && (
          <p className="mt-3 text-xs text-muted">
            The billing portal is available once you have an active subscription.
          </p>
        )}
      </div>

      {/* ── Reactivation card — for subscribers who let their subscription fully expire ── */}
      {!hasSubscription && !sub.gracePeriod.isInGracePeriod && sub.hasUsedTrial && (
        <div className="rounded-lg border border-primary/20 bg-primary/5 p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-bold text-foreground">Welcome back!</h2>
              <p className="mt-1 text-sm text-muted">
                Re-subscribe to restore your organizations, game server listings, and all BlockyNetworks features.
                No new trial — your subscription starts immediately.
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              {PRICING_PLANS.map((plan) => (
                <Button
                  key={plan.id}
                  variant={plan.badge ? "default" : "outline"}
                  className="gap-1.5"
                  onClick={() => handleSelectPlan(plan.id as PricingPlanId)}
                  disabled={loadingPlan !== null}
                >
                  {loadingPlan === plan.id ? "Processing..." : `${plan.name} — $${plan.priceMonthly.toFixed(2)}/mo`}
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Billing History ── */}
      <BillingHistorySection clerkUserId={clerkUserId ?? null} />

      {/* ── Cancel button — opens inline retention modal ── */}
      {isActiveSubscriber && !sub.cancelAtPeriodEnd && (
        <div className="text-center">
          <button
            type="button"
            className="text-xs text-muted hover:text-foreground hover:underline disabled:opacity-50"
            onClick={() => setShowCancelModal(true)}
            disabled={cancelling}
          >
            {cancelling ? "Cancelling..." : "Cancel your subscription"}
          </button>
        </div>
      )}

      {/* Plan change confirmation dialog */}
      <ConfirmDialog
        open={pendingPlanChange !== null}
        title={dialogProps.title}
        description={dialogProps.description}
        confirmText={dialogProps.confirmText}
        cancelText="Keep current plan"
        variant={pendingPlanChange?.isDowngrade ? "destructive" : "default"}
        onConfirm={handleConfirmPlanChange}
        onCancel={() => setPendingPlanChange(null)}
      />

      {/* Cancellation confirmation modal with retention messaging */}
      <CancellationConfirmationModal
        open={showCancelModal}
        tier={tier}
        periodEndDate={sub.currentPeriodEnd ? new Date(sub.currentPeriodEnd).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : null}
        activeOrgCount={sub.activeOrgCount}
        serverLimit={serverLimit}
        onConfirm={handleConfirmCancel}
        onCancel={() => setShowCancelModal(false)}
      />
    </div>
  );
}
